'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn } from '@/lib/animations';
import { DollarSign, Clock, Shield, Scale, Briefcase, TrendingUp } from 'lucide-react';

const benefits = [
  {
    title: 'Duty Deferral',
    description: 'Delay payment of duties until goods enter U.S. commerce, improving cash flow.',
    icon: DollarSign,
  },
  {
    title: 'Duty Elimination',
    description: 'Eliminate duties on goods that are re-exported, maximizing international trade profits.',
    icon: Clock,
  },
  {
    title: 'Duty Reduction',
    description: 'Pay the lower duty rate between component materials and finished product.',
    icon: TrendingUp,
  },
  {
    title: 'Compliance Management',
    description: 'Ensure regulatory compliance with our expert oversight and documentation.',
    icon: Shield,
  },
  {
    title: 'Weekly Entry Savings',
    description: 'Reduce merchandise processing fees with weekly entry filings.',
    icon: Briefcase,
  },
  {
    title: 'Quota Advantage',
    description: 'Store merchandise in FTZ until quota opens, gaining competitive advantage.',
    icon: Scale,
  },
];

export function FTZBenefits() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0,
    rootMargin: '100px 0px',
  });

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
          variants={fadeIn('up')}
          className="text-center mb-16"
        >
          <h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">FTZ Benefits</h2>
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Maximize Your International Trade Potential
          </h3>
          <p className="max-w-2xl mx-auto text-gray-700">
            Discover how our Foreign-Trade Zone services can transform your international trade operations and boost your bottom line.
          </p>
        </motion.div>
        
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
          variants={fadeIn('up')}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mb-6 text-brand-600">
                <benefit.icon size={24} />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h4>
              <p className="text-gray-700">{benefit.description}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}